import React, { useCallback, useEffect, useState } from 'react';

import { EOTPType, IRequestOtpParams } from '@webtrader/core/api/account/request-otp';
import { useRequestTimer } from '@webtrader/core/hooks/utils/use-request-timer';
import { useTranslation } from '@webtrader/core/i18n';
import { authService } from '@webtrader/core/services/auth';

import { AlertManager } from '@/components/Alert/Manager';
import Button from '@/components/Button';
import ValidationError from '@/components/FormValidationError';
import TextInput from '@/components/TextInput';

import { StyledLoginFormField } from '../styled';

interface ILoginOtpFormVerificationFieldProps {
  dialCode?: string;
  email?: string;
  isDisabled?: boolean;
  isError?: boolean;
  isOtpLoading: boolean;
  otpType: EOTPType | null;
  phoneNumber?: string;
  value?: string;
  onVerificationCodeChange?: (value?: string) => void;
}

const LoginOtpFormVerificationField: FCC<ILoginOtpFormVerificationFieldProps> = (props) => {
  const {
    dialCode,
    email,
    isDisabled,
    isError = false,
    isOtpLoading,
    otpType,
    phoneNumber,
    value,
    onVerificationCodeChange
  } = props;
  const { t } = useTranslation();
  const [durationMs, setDurationMs] = useState(1 * 60 * 1000); // 1 Min

  const { buttonText, remainingTime, startTimer, resetTimer } = useRequestTimer({
    requestText: t('Send Code'),
    timerPrefixText: '',
    durationMs: durationMs
  });

  const isVerificationCodeDisabled = isDisabled || remainingTime > 0 || isOtpLoading;

  const onClickRequest = useCallback(async () => {
    if (!otpType) {
      return;
    }

    if (otpType === EOTPType.EMAIL && !email) {
      return;
    }

    if (otpType === EOTPType.SMS && (!phoneNumber || !dialCode)) {
      return;
    }

    const otpParams: IRequestOtpParams = {
      type: otpType,
      email,
      phoneNumber: phoneNumber?.toString(),
      countryCode: dialCode
    };

    const { success, message, data } = await authService.requestOtp(otpParams);

    if (success) {
      data?.timeoutInSec && setDurationMs(data.timeoutInSec * 1000);

      // Wait for state update, then start timer
      setTimeout(() => startTimer(), 0);

      AlertManager().alert({
        type: 'success',
        message
      });
    } else {
      AlertManager().alert({
        message
      });
    }
  }, [dialCode, email, otpType, phoneNumber, startTimer]);

  useEffect(() => {
    // Reset timer when tab changes
    return () => resetTimer();
  }, [resetTimer]);

  return (
    <StyledLoginFormField>
      <TextInput
        label={t('Verification Code')}
        value={value}
        onChangeText={onVerificationCodeChange}
        suffix={
          <Button
            variant="subtle"
            showOrderTypeColors={false}
            onPress={onClickRequest}
            disabled={isVerificationCodeDisabled}
            textColor={isVerificationCodeDisabled ? 'light' : 'default'}
          >
            {buttonText}
          </Button>
        }
      />
      <ValidationError showValidation={isError} message={t('Please enter a valid verification code')} />
    </StyledLoginFormField>
  );
};

export default LoginOtpFormVerificationField;
